/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import {
  Button,
  Checkbox,
  Divider,
  Grid,
  StandaloneCheckbox,
  Tab,
  TabContext,
  TabPanel,
  Tabs,
  TemporaryDrawer,
  TextField,
  Typography2,
} from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { SearchIcon } from '@coursera/cds-icons';

import type { ProgramSelectionMap } from 'bundles/enterprise-admin-drawers/components/ExportDrawer';
import type { ProgramList, ProgramListItem } from 'bundles/enterprise-admin-drawers/components/types';

import _t from 'i18n!nls/enterprise-admin-drawers';

const styles: Record<string, Interpolation<Theme>> = {
  buttonWrapper: () => css`
    margin-right: var(--cds-spacing-200);
  `,
  textField: () => css`
    margin-bottom: var(--cds-spacing-200);
  `,
  noResultsWrapper: () => css`
    margin-top: var(--cds-spacing-400);
  `,
};

type Props = {
  programs: ProgramList;
  selectedProgramsMap: ProgramSelectionMap;
  onBack: () => void;
  onApply: (newlySelectedPrograms: ProgramSelectionMap) => void;
  onClose: () => void;
};

const TAB_NAMES = {
  SEARCH_RESULTS: '0',
  SELECTED_PROGRAMS: '1',
};

const ExportDrawerProgramSelectionView = ({ programs, selectedProgramsMap, onApply, onBack, onClose }: Props) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentTab, setCurrentTab] = useState<string>(TAB_NAMES.SEARCH_RESULTS);
  const [searchResults, setSearchResults] = useState<ProgramList>(programs);
  const [currentlySelectedPrograms, setCurrentlySelectedFilters] = useState<ProgramSelectionMap>({
    ...selectedProgramsMap,
  });

  const handleTextFieldOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    setSearchTerm(target.value);
    const relatedPrograms = programs.filter((program) =>
      program.name.toLocaleLowerCase().includes(target.value.toLocaleLowerCase())
    );
    setSearchResults(relatedPrograms);
  };

  const handleOnCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
    const { target } = event;
    setCurrentlySelectedFilters((prevState) => ({
      ...prevState,
      [target.value]: { ...prevState[target.value], isSelected: isChecked },
    }));
  };

  const handleCheckAll = (_: React.ChangeEvent<HTMLInputElement>, isChecked: boolean) => {
    setCurrentlySelectedFilters((prevState) => {
      return searchResults.reduce(
        (acc, program) => ({ ...acc, [program.id]: { ...prevState[program.id], isSelected: isChecked } }),
        prevState
      );
    });
  };

  const getAllSearchResultsSelected = () => {
    return searchResults.every((program) => currentlySelectedPrograms[program.id].isSelected);
  };

  const renderProgramName = (program: ProgramListItem) => {
    if (program.isArchived) return _t(`#{programName} (archived)`, { programName: program.name });
    return program.name;
  };

  const selectedProgramIds: string[] = Object.values(currentlySelectedPrograms).reduce(
    (acc, program) => (program.isSelected ? [...acc, program.id] : acc),
    [] as string[]
  );

  const { HeadingGroup, Content, Actions } = TemporaryDrawer;
  return (
    <TemporaryDrawer open={true} anchor="right" sticky="actions" onClose={onClose}>
      <HeadingGroup data-testid="program-selection-view-header">{_t('Programs')}</HeadingGroup>
      <Content css={{ overflow: 'scroll' }}>
        <Grid container direction="row">
          <TextField
            fullWidth
            prefix={<SearchIcon />}
            aria-label={_t('Search programs')}
            placeholder={_t('Search programs')}
            onChange={handleTextFieldOnChange}
            value={searchTerm}
            css={styles.textField}
          />
        </Grid>
        <TabContext value={currentTab}>
          <Tabs aria-label={_t('Search results and selected programs')}>
            <Tab
              label={_t('Search results (#{numOfResults})', { numOfResults: searchResults.length })}
              value={TAB_NAMES.SEARCH_RESULTS}
              onClick={() => setCurrentTab(TAB_NAMES.SEARCH_RESULTS)}
            />
            <Tab
              label={_t('Selected (#{numOfSelected})', { numOfSelected: selectedProgramIds.length })}
              value={TAB_NAMES.SELECTED_PROGRAMS}
              data-testid="selected-programs-tab"
              onClick={() => setCurrentTab(TAB_NAMES.SELECTED_PROGRAMS)}
            />
          </Tabs>
          <TabPanel value={TAB_NAMES.SEARCH_RESULTS}>
            <Grid container direction="column">
              {searchResults.length ? (
                <>
                  <StandaloneCheckbox
                    value="select-all-collections"
                    label={_t('Select all')}
                    onChange={handleCheckAll}
                    checked={getAllSearchResultsSelected()}
                  />
                  <Divider />
                </>
              ) : (
                <Grid container item justifyContent="center" css={styles.noResultsWrapper}>
                  <Typography2 component="p">{_t('No results found')}</Typography2>
                </Grid>
              )}
              {Boolean(searchResults.length) &&
                searchResults.map((result) => (
                  <Checkbox
                    label={renderProgramName(result)}
                    value={result.id}
                    checked={currentlySelectedPrograms[result.id].isSelected}
                    onChange={handleOnCheckboxChange}
                    key={result.id}
                  />
                ))}
            </Grid>
          </TabPanel>
          <TabPanel value={TAB_NAMES.SELECTED_PROGRAMS}>
            <Grid container direction="column">
              {selectedProgramIds.length ? (
                selectedProgramIds.map((id) => (
                  <Checkbox
                    label={renderProgramName(currentlySelectedPrograms[id])}
                    value={id}
                    checked={currentlySelectedPrograms[id]?.isSelected}
                    key={`selected~${id}`}
                    onChange={handleOnCheckboxChange}
                  />
                ))
              ) : (
                <Grid item container justifyContent="center" css={styles.noResultsWrapper}>
                  <Typography2 component="p" variant="bodyPrimary">
                    {_t('No filters selected')}
                  </Typography2>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        </TabContext>
      </Content>
      <Actions>
        <Button variant="secondary" onClick={onBack} css={styles.buttonWrapper}>
          {_t('Back')}
        </Button>
        <Button
          variant="primary"
          onClick={() => onApply(currentlySelectedPrograms)}
          data-testid="export-drawer-apply-program-filter-button"
        >
          {_t('Apply')}
        </Button>
      </Actions>
    </TemporaryDrawer>
  );
};

export default ExportDrawerProgramSelectionView;
